import React from 'react';
import { Box } from '@mui/material';
import { BooleanQuestionInsight, CombinedBoolean } from '../insightsTypes';
import YesRateBars from '@/components/globalPrompts/insights/charts/YesRateBars';
import DonutChart from '@/components/common/charts/DonutChart';
import OverviewShell from './slices/OverviewShell';
import MetricHeading from './slices/MetricHeading';
import NoInsightsOverview from './slices/NoInsightsOverview';
import { COMBINED_BAR_COLOR, COMBINED_TEXT_COLOR, NEUTRAL_TRACK } from '../insightsTheme';
import { getQuestionLabel } from '../shared/getQuestionLabel';

// Describes combineBooleanInsights on the server (services/v2/helpers/combineInsights.ts).
// Keep the two in step if the formula changes.
const COMBINED_YES_RATE_INFO = 'The share of every answer that was yes, so questions with more answers count for more.';

interface BooleanCategoryOverviewProps {
    // Server-computed rollup for all yes/no questions in the section.
    combined: CombinedBoolean;
    // Still needed for the "Yes rate by question" bars (per-question reshape, not aggregation).
    questions: BooleanQuestionInsight[];
}

const BooleanCategoryOverview: React.FC<BooleanCategoryOverviewProps> = ({ combined, questions }) => {
    if (combined.overall.responseCount === 0) {
        return <NoInsightsOverview />;
    }

    // Only questions with responses carry an insight; skip new/unanswered ones.
    // YesRateBars sorts by yes rate and keeps the top 5 (limit prop below).
    const byQuestion = questions
        .filter((question) => question.overall.responseCount > 0)
        .map((question) => ({
            // Visible bar label: short title if set, otherwise truncated question text.
            label: getQuestionLabel(question),
            // Hover tooltip: always the full question text.
            questiontext: question.questionText,
            percentage: question.overall.yesPct,
            responses: question.overall.responseCount,
            yesCount: question.overall.yesCount,
        }));

    return (
        <OverviewShell>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' }, gap: 4, alignItems: 'center' }}>
                <Box>
                    <MetricHeading info={COMBINED_YES_RATE_INFO}>Combined yes rate</MetricHeading>
                    <Box sx={{ mt: 1.5 }}>
                        <DonutChart
                            slices={[
                                {
                                    label: 'Yes',
                                    percentage: combined.overall.yesPct,
                                    count: combined.overall.yesCount,
                                    color: COMBINED_BAR_COLOR,
                                },
                                {
                                    label: 'No',
                                    percentage: 100 - combined.overall.yesPct,
                                    count: combined.overall.noCount,
                                    color: NEUTRAL_TRACK,
                                },
                            ]}
                            centerValue={`${Math.round(combined.overall.yesPct)}%`}
                            centerLabel="Yes"
                            centerColor={COMBINED_TEXT_COLOR}
                            size={132}
                            ringWidth={18}
                        />
                    </Box>
                </Box>
                <Box sx={{ borderLeft: { md: '1px solid' }, borderColor: { md: 'divider' }, pl: { md: 4 } }}>
                    <MetricHeading>Top 5 questions by yes rate</MetricHeading>
                    <Box sx={{ mt: 1.5 }}>
                        {/* Combined view: one calm blue, no good/bad colour coding. */}
                        <YesRateBars rows={byQuestion} limit={5} barColor={COMBINED_BAR_COLOR} valueColor={COMBINED_TEXT_COLOR} />
                    </Box>
                </Box>
            </Box>
        </OverviewShell>
    );
};

export default BooleanCategoryOverview;
